import React, {useEffect, useRef, useState} from "react";
import {Table, Tag, Button, Progress, Space, Row, Col, Select, Avatar, Typography} from 'antd';
import {CheckCircleOutlined, LoadingOutlined, AlertOutlined, DownloadOutlined, ClockCircleOutlined} from "@ant-design/icons";
import api from "../api";
import TimeAgo from "../helpers/TimeAgo";
import User from "../helpers/User";
import {useTranslation} from "react-i18next";
import PaginationTotal from "../widgets/PaginationTotal";
import DateRangePicker from "../widgets/DateRangePicker";

export default () => {
    const {t} = useTranslation();
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({current: 1, pageSize: 10});
    const [loading, setLoading] = useState(false);
    const [dates, setDates] = useState();
    const [userId, setUserId] = useState();
    const [userOptions, setUserOptions] = useState([]);

    const params = useRef({})

    const handleUserSearch = (val) => {
        if(!val) return;
        api('/api/memberships', {params: {q: val, tester: false}}).then(res => {
            setUserOptions(res.data.map(m => ({
                value: m.user.id,
                label: (
                    <span title={m.user.email}>
                        <Avatar src={m.user.avatar_url} size="small" style={{marginRight: 6}}>{m.user.initials}</Avatar>
                        {m.user.name} <Typography.Text type="secondary">{m.user.email}</Typography.Text>
                    </span>
                ),
            })))
        })
    }

    const columns = [
        {
            title: t('created', 'Created'),
            dataIndex: 'created_at',
            sorter: true,
            render: date => <TimeAgo date={date}/>
        },
        {
            title: t('user', 'User'),
            render: record => <User user={record.user}/>
        },
        {
            title: t('assets', 'Assets'),
            dataIndex: 'total_assets',
            sorter: true
        },
        {
            title: t('status', 'Status'),
            dataIndex: 'aasm_state',
            render: (state) => {
                if(state === 'processed') return <Tag color="green" icon={<CheckCircleOutlined/>}>{t('completed', 'Completed')}</Tag>
                if(state === 'processing') return <Tag color="blue" icon={<LoadingOutlined/>}>{t('processing', 'Processing')}</Tag>
                if(state === 'errored') return <Tag color="red" icon={<AlertOutlined/>}>{t('errored', 'Errored')}</Tag>
                return <Tag icon={<ClockCircleOutlined/>}>{t('pending', 'Pending')}</Tag>
            }
        },
        {
            title: t('progress', 'Progress'),
            render: record => {
                if(record.aasm_state === 'processed') return <>100%</>
                if(record.aasm_state === 'processing') return <Progress percent={record.progress || 0} size="small" status="active"/>
                return null
            }
        },
        {
            title: t('download', 'Download'),
            render: record => {
                if(record.aasm_state === 'processed' && record.s3_key) {
                    return (
                        <Button
                            type="link"
                            icon={<DownloadOutlined/>}
                            href={`/api/downloads/table_data?key=${record.s3_key}`}
                        >
                            CSV
                        </Button>
                    )
                }
                return null
            }
        }
    ];

    const handleTableChange = (pag, filters, sorter) => {
        params.current = {
            sortField: sorter?.field,
            sortOrder: sorter?.order,
            pagination: pag || pagination,
            dates,
            user_id: userId,
        }
        loadTableData();
    }

    const loadTableData = () => {
        setLoading(true);
        api.get('/api/meta_downloads', {params: params.current}).then(res => {
            setData(res.data)
            setPagination({
                ...params.current.pagination,
                total: res.headers['total-entries']
            })
            setLoading(false)
        })
    }

    useEffect(handleTableChange, [dates, userId]);

    return (
        <Space direction="vertical" style={{width: '100%'}}>
            <Row gutter={8}>
                <Col lg={4} xs={24}>
                    <DateRangePicker onChange={dates => setDates(dates)}/>
                </Col>
                <Col lg={8} xs={24}>
                    <Select
                        showSearch
                        allowClear
                        placeholder={t('placeholder-filter-user', 'Filter by user...')}
                        popupMatchSelectWidth={false}
                        style={{width: '100%'}}
                        filterOption={false}
                        onSearch={handleUserSearch}
                        onChange={setUserId}
                        value={userId}
                        options={userOptions}
                    />
                </Col>
            </Row>

            <Table
                bordered
                scroll={{x: true}}
                size="small"
                columns={columns}
                rowKey={record => record.id}
                dataSource={data}
                pagination={pagination}
                loading={loading}
                onChange={handleTableChange}
                footer={() => (
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <PaginationTotal pagination={pagination}/>
                    </div>
                )}
            />
        </Space>
    );
}
